/**
 * Input abstraction layer.
 * Maps keyboard, accelerometer (mobile tilt), and touch to game actions.
 *
 * On desktop: keyboard controls
 * On mobile: tilt to steer/tuck/brake, tap to hop
 */

export type GameAction =
  | 'steerLeft'
  | 'steerRight'
  | 'tuck'
  | 'brake'
  | 'jump'

const KEY_MAP: Record<string, GameAction> = {
  ArrowLeft: 'steerLeft',
  KeyA: 'steerLeft',
  ArrowRight: 'steerRight',
  KeyD: 'steerRight',
  ArrowUp: 'tuck',
  KeyW: 'tuck',
  ArrowDown: 'brake',
  KeyS: 'brake',
  Space: 'jump',
}

// Accelerometer thresholds (degrees of tilt)
const STEER_DEAD_ZONE = 18    // large dead zone — only intentional tilts steer
const STEER_MAX = 40           // full steer at this tilt
const TUCK_THRESHOLD = 5       // start tucking at this tilt
const TUCK_MAX = 25            // max tuck intensity at this tilt
const BRAKE_THRESHOLD = 8      // brake threshold (opposite direction from tuck)

export class InputManager {
  private activeActions = new Set<GameAction>()
  private _isMobile = false
  private _accelerometerActive = false
  private _gamma = 0   // left/right tilt
  private _beta = 0    // forward/back tilt
  private _neutralBeta = -1  // calibrated "resting" beta (set on first reading)
  private _calibrated = false
  private _tuckIntensity = 0 // 0 = no tuck, 1 = max tuck (proportional to tilt)
  private debugEl: HTMLDivElement | null = null

  constructor() {
    // Keyboard input (always active)
    window.addEventListener('keydown', (e) => {
      const action = KEY_MAP[e.code]
      if (action) {
        this.activeActions.add(action)
        e.preventDefault()
      }
    })

    window.addEventListener('keyup', (e) => {
      const action = KEY_MAP[e.code]
      if (action) {
        this.activeActions.delete(action)
      }
    })

    // Detect mobile
    this._isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    if (this._isMobile) {
      this.setupTouch()
      // Debug overlay for mobile accelerometer testing
      this.debugEl = document.createElement('div')
      this.debugEl.style.cssText = `
        position: fixed; bottom: 80px; left: 10px; font-size: 11px;
        color: white; background: rgba(0,0,0,0.5); padding: 6px 10px;
        border-radius: 6px; z-index: 300; pointer-events: none;
        font-family: monospace;
      `
      this.debugEl.textContent = 'Accel: waiting...'
      document.body.appendChild(this.debugEl)
    }
  }

  get isMobile(): boolean {
    return this._isMobile
  }

  get accelerometerActive(): boolean {
    return this._accelerometerActive
  }

  /**
   * Public method to set up accelerometer listeners.
   * Called by StartScreen AFTER iOS permission is granted.
   */
  setupAccelerometerPublic(): void {
    this.setupAccelerometer()
  }

  private setupAccelerometer(): void {
    window.addEventListener('deviceorientation', (e) => {
      this._accelerometerActive = true
      this._gamma = e.gamma ?? 0  // left/right (-90 to 90)
      this._beta = e.beta ?? 0    // forward/back (0 to 180)

      // Calibrate: capture how the player holds the phone as "neutral"
      // First few readings set the baseline
      if (!this._calibrated) {
        this._neutralBeta = this._beta
        this._calibrated = true
      }

      // Tilt RELATIVE to how the player holds the phone
      const relativeBeta = this._beta - this._neutralBeta

      // Clear keyboard-style actions and set from accelerometer
      this.activeActions.delete('steerLeft')
      this.activeActions.delete('steerRight')
      this.activeActions.delete('tuck')
      this.activeActions.delete('brake')

      // Steering: gamma is left/right tilt
      if (this._gamma < -STEER_DEAD_ZONE) {
        this.activeActions.add('steerLeft')
      } else if (this._gamma > STEER_DEAD_ZONE) {
        this.activeActions.add('steerRight')
      }

      // Tuck/brake: flipped — negative relativeBeta = tilt forward = tuck
      this._tuckIntensity = 0
      if (relativeBeta < -TUCK_THRESHOLD) {
        this.activeActions.add('tuck')
        this._tuckIntensity = Math.min((-relativeBeta - TUCK_THRESHOLD) / (TUCK_MAX - TUCK_THRESHOLD), 1)
      } else if (relativeBeta > BRAKE_THRESHOLD) {
        this.activeActions.add('brake')
      }

      // Debug display
      if (this.debugEl) {
        const actions = [...this.activeActions].join(', ') || 'cruising'
        this.debugEl.textContent = `γ:${this._gamma.toFixed(0)} β:${relativeBeta.toFixed(0)} (n:${this._neutralBeta.toFixed(0)}) | ${actions}`
      }
    })

    // Also try devicemotion as a fallback
    window.addEventListener('devicemotion', (e) => {
      if (!this._accelerometerActive && e.accelerationIncludingGravity) {
        // Fallback: use devicemotion if deviceorientation doesn't fire
        const acc = e.accelerationIncludingGravity
        this._accelerometerActive = true

        this.activeActions.delete('steerLeft')
        this.activeActions.delete('steerRight')

        // X acceleration = left/right tilt
        if (acc.x !== null && acc.x < -1) {
          this.activeActions.add('steerLeft')
        } else if (acc.x !== null && acc.x > 1) {
          this.activeActions.add('steerRight')
        }

        if (this.debugEl) {
          this.debugEl.textContent = `motion x:${acc.x?.toFixed(1)} y:${acc.y?.toFixed(1)}`
        }
      }
    })
  }

  private setupTouch(): void {
    // Tap anywhere = hop
    window.addEventListener('touchstart', (e) => {
      // Don't trigger hop if tapping the HUD/menu
      const target = e.target as HTMLElement
      if (target.tagName === 'A' || target.tagName === 'BUTTON' || target.closest('[data-no-hop]')) {
        return
      }

      this.activeActions.add('jump')
      e.preventDefault()
    }, { passive: false })

    window.addEventListener('touchend', () => {
      this.activeActions.delete('jump')
    })
  }

  /**
   * Get the raw tilt value for proportional steering (0-1).
   */
  getSteerIntensity(): number {
    if (!this._accelerometerActive) return 0
    const absGamma = Math.abs(this._gamma)
    if (absGamma < STEER_DEAD_ZONE) return 0
    return Math.min((absGamma - STEER_DEAD_ZONE) / (STEER_MAX - STEER_DEAD_ZONE), 1)
  }

  /**
   * Get tuck intensity (0-1). More tilt = higher value.
   * On keyboard, returns 1 (full tuck) when W is pressed.
   */
  getTuckIntensity(): number {
    if (this._accelerometerActive) return this._tuckIntensity
    return this.activeActions.has('tuck') ? 1 : 0
  }

  isActive(action: GameAction): boolean {
    return this.activeActions.has(action)
  }
}
